import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart2, 
  PieChart, 
  Cpu,
  Smile,
  Shield, 
  Globe, 
  LineChart, 
  DollarSign
} from 'lucide-react';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0E23] text-white">
      {/* SEO Meta Etiketleri */}
      <Helmet>
        <title>Smart Portfoy | Yatırım ve Finans Portföy Takip Platformu</title>
        <meta 
          name="description" 
          content="Smart Portfoy ile tüm yatırımlarınızı tek bir yerden yönetin. Gerçek performansınızı öğrenin ve yeniden dengeleme alarmları ile portföyünüzü optimize edin." 
        />
        <meta 
          name="keywords" 
          content="portföy takibi, yatırım, borsa, hisse senedi, ETF, yatırım fonu, yeniden dengeleme, risk yönetimi" 
        />
        
        {/* Open Graph Meta Etiketleri */}
        <meta property="og:title" content="Smart Portfoy | Akıllı Yönetim, Otomatik Dengeleme, Sürekli Kontrol" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
        <meta property="og:image" content={`${window.location.origin}/portfolio-illustration.png`} />
        <meta 
          property="og:description" 
          content="Tüm yatırımlarınızı tek bir yerden takip edin, gerçek performansınızı öğrenin ve yeniden dengeleme alarmları ile portföyünüzü optimize edin." 
        />
        <meta property="og:site_name" content="Smart Portfoy" />
        
        {/* Twitter Card Meta Etiketleri */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Smart Portfoy | Akıllı Yatırım Yönetimi" />
        <meta 
          name="twitter:description" 
          content="Tüm yatırımlarınızı tek bir yerden takip edin, gerçek performansınızı öğrenin." 
        />
        <meta name="twitter:image" content={`${window.location.origin}/portfolio-illustration.png`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.origin} />
        
        {/* Yerelleştirme Meta Etiketi */}
        <meta property="og:locale" content="tr_TR" />
        
        {/* İndeksleme Direktifleri */}
        <meta name="robots" content="index, follow" />
        
        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/logo192.png" />
      </Helmet>

      {/* Navbar */}
 
      {/* Hero Bölümü */}
      <div className="relative overflow-hidden bg-[#0A0E23] py-24">
        {/* Gradient Arkaplan Çizgileri */}
        <div className="absolute inset-0">
          <div className="absolute bottom-0 right-0 w-full h-full">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 1200 800"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMaxYMid slice"
            >
              <path
                d="M400,0 C400,400 800,600 1200,600"
                stroke="url(#gradient1)"
                strokeWidth="2"
                fill="none"
                className="opacity-20"
              />
              <path
                d="M300,0 C300,500 700,700 1200,700"
                stroke="url(#gradient1)"
                strokeWidth="2"
                fill="none"
                className="opacity-15"
              />
              <path
                d="M200,0 C200,600 600,800 1200,800"
                stroke="url(#gradient1)"
                strokeWidth="2"
                fill="none"
                className="opacity-10"
              />
              <defs>
                <linearGradient id="gradient1" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#00A3FF" />
                  <stop offset="100%" stopColor="#8B5CF6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            {/* Sol taraf - Yazılar */}
            <div className="lg:w-1/2 text-left lg:pr-12 relative z-10">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight">
                <span className="text-[#00A3FF]">Akıllı yönetim</span>{" "}
                
                <span className="text-[#8B5CF6]">otomatik dengeleme</span>
                <br />
                <span className="text-white"> sürekli kontrol</span>
              </h1>
              
              <div className="mt-6 space-y-4">
                <div className="flex items-center text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-[#00A3FF] mr-3"></div>
                  <p>Tüm yatırımlarınız tek bir yerde</p>
                </div>
                <div className="flex items-center text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-[#00A3FF] mr-3"></div>
                  <p>Gerçek performansınızı öğrenin</p>
                </div>
                <div className="flex items-center text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-[#00A3FF] mr-3"></div>
                  <p>Yeniden Dengeleme Alarmları</p>
                </div>
              </div>

              <div className="mt-10 flex gap-4">
                <button
                  onClick={() => navigate('/register')}
                  className="px-6 py-3 bg-[#00A3FF] text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Başlayın
                </button>
           
              </div>
         
            </div>
            
            {/* Sağ taraf - Görseller */}
            <div className="lg:w-1/2 mt-12 lg:mt-0 relative z-10">
              <img
                src="/portfolio-illustration.png"
                alt="Portföy Dashboard Önizleme"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Özellikler Bölümü */}
      <div className="relative py-24 bg-[#151934] overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(/globe-bg.png)',
            backgroundSize: '100% auto',
            backgroundPosition: 'center 40%'
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <h2 className="text-center text-3xl lg:text-4xl font-bold mb-16">
            <span className="text-white">Binlerce hisse senedi, </span>
            <span className="text-[#00A3FF]">ETF ve fonu</span>
            <br />
            <span className="text-white">tüm dünyadan takip edin </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Aracı Kurumlar Kartı */}
            <div className="bg-[#1E2444]/80 backdrop-blur-sm rounded-lg p-6 transition-transform hover:-translate-y-1">
              <div className="flex items-baseline mb-4">
                <span className="text-[#00A3FF] text-4xl font-bold">100%</span>
                <span className="text-white ml-2 text-xl">Risk Yönetimi</span>
              </div>
              <p className="text-gray-300">
                Hedef Portföyünüze Uygun hisse Senetleri, ETF, Fon ve Emtilar için Yeniden Dengeleme uyarıları alın..
              </p>
            </div>

            {/* Borsalar Kartı */}
            <div className="bg-[#1E2444]/80 backdrop-blur-sm rounded-lg p-6 transition-transform hover:-translate-y-1">
              <div className="flex items-baseline mb-4">
                <span className="text-[#00A3FF] text-4xl font-bold">70+</span>
                <span className="text-white ml-2 text-xl">borsa</span>
              </div>
              <p className="text-gray-300">
                Dünyadaki tüm önemli borsalarda işlem gören hisse senetleri, ETF'ler ve yatırım fonlarını takip edin
              </p>
            </div>

            {/* Para Birimleri Kartı */}
            <div className="bg-[#1E2444]/80 backdrop-blur-sm rounded-lg p-6 transition-transform hover:-translate-y-1">
              <div className="flex items-baseline mb-4">
                <span className="text-[#00A3FF] text-4xl font-bold">7+</span>
                <span className="text-white ml-2 text-xl">para birimi</span>
              </div>
              <p className="text-gray-300">
                Yatırımlarınızı farklı para birimlerinde ekleyin - USD, EUR, TRY ve portföy performansınızı istediğiniz para biriminde görüntüleyin
              </p>
            </div>
          </div>
        </div>
      </div>

      <MainFeatures />
      <Footer />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="relative p-6 rounded-xl backdrop-blur-sm transition-all duration-300 hover:scale-105 
                  border border-gray-200 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500
                  group cursor-pointer">
    {/* Blue glow effect */}
    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-blue-500/0 
                  opacity-0 group-hover:opacity-100 transition-opacity duration-300 
                  blur-xl pointer-events-none" />
    
    <div className="relative z-10">
      <div className="w-12 h-12 mx-auto mb-4 text-blue-500 flex items-center justify-center">
        {icon}
      </div>
      <h3 className="text-lg font-medium text-center mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400 text-center text-sm">{description}</p>
    </div>
  </div>
);

const MainFeatures = () => {
  const features = [
    {
      icon: <PieChart className="w-8 h-8" />,
      title: "Portföy Analizi",
      description: "Profesyonel analistler günde bir düzine enstrümanı izleyebilir. Smart Portfoy ticaret uygulamaları on binlerce finansal enstrümanı gerçek zamanlı olarak izler."
    },
    {
      icon: <BarChart2 className="w-8 h-8" />,
      title: "Performans Takibi",
      description: "Yatırımlarınızın getirisini ve risk seviyelerini daha etkili bir şekilde değerlendirebilmeniz için, getirilerinizi düzenli olarak takip edin ve risk metriklerinizi analiz edin. Böylece, finansal hedeflerinize ulaşma sürecinde daha bilinçli kararlar alabilir ve stratejilerinizi ihtiyaçlarınıza göre optimize edebilirsiniz."
    },
    {
      icon: <DollarSign  className="w-8 h-8" />,
      title: "Temettü Takibi",
      description: "Temettü gelirlerinizi takip edin ve analiz edin"
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "Hesaplama Gücünü Açığa Çıkarın",
      description: "Profesyonel analistler günde bir düzine enstrümanı izleyebilir. Smart Portfoy ticaret uygulamaları on binlerce finansal enstrümanı gerçek zamanlı olarak izler."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Riski En Aza İndirin",
      description: "Smart Portfoy yenilikçi Risk Hesaplayıcısı ile kaldıraçlı piyasalardaki riski ve potansiyel aşırı maruziyeti etkili bir şekilde yönetin."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Küresel Pazar Kapsamı",
      description: "Smart Portfoy sinyalleri, hisse senetleri, endeksler, dövizler, külçe, metaller, emtialar, ETF'ler, opsiyonlar ve kripto paralar dahil olmak üzere küresel piyasaların geniş bir kapsamını sağlar."
    },
    {
      icon: <LineChart className="w-8 h-8" />,
      title: "Teknik Analiz",
      description: "Smart portfoy Teknik Analizi, potansiyel fiyat hareketlerini ortaya çıkarmak için geçmiş verileri çözümler. Üçgenler, Baş ve omuzlar ve kanallar gibi teknik grafik desenlerine dalın."
    },
    {
      icon: <PieChart className="w-8 h-8" />,
      title: "Temel Analiz",
      description: "Smart Portfoy Temel Analizi, içsel değeri belirlemek için finansal metrikleri ve makroekonomik göstergeleri hesaba katarak varlıklara dair bütünsel bir bakış açısı sunar."
    }
    ,
    {
      icon: <Smile   className="w-8 h-8" />,
      title: "Duygu Analizi",
      description: "Smart Portfoy Duygu Analizi, çeşitli kaynaklardan iyimserlik ve kötümserliği ölçmek için gelişmiş araçlar kullanarak piyasanın nabzını tutar. Piyasa haberleri analizi aracılığıyla para birimleri ve ABD hisse senetleri üzerindeki duygu etkisini keşfedin ve Haber Duygumuzla gerçek zamanlı kamuoyuna erişin, temel ruh hallerini ve potansiyel piyasa hareketlerini ayırt edin."
    }
  ];

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12 text-white">Ana Özellikler</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LandingPage;